import React from 'react';
import { Progress, Badge } from '@ai-content-hub/content-kit';
import { cn } from '../lib/utils';
import { formatTime, formatTimeLong } from '../engine/timer';

export interface TimerDisplayProps {
  remaining: number;
  total?: number;
  isRunning?: boolean;
  isWarning?: boolean;
  isExpired?: boolean;
  showProgress?: boolean;
  format?: 'short' | 'long';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-2xl',
};

/**
 * TimerDisplay - Shows countdown timer
 */
export function TimerDisplay({
  remaining,
  total,
  isRunning = false,
  isWarning = false,
  isExpired = false,
  showProgress = false,
  format = 'short',
  size = 'md',
  className,
}: TimerDisplayProps): React.ReactElement {
  const formattedTime = format === 'long' 
    ? formatTimeLong(remaining) 
    : formatTime(remaining);

  const progress = total ? ((total - remaining) / total) * 100 : 0;

  return (
    <div className={cn('timer-display', 'inline-flex flex-col items-center gap-1', className)}>
      <div
        className={cn(
          'timer-time',
          'font-mono font-bold tabular-nums',
          sizeClasses[size],
          isExpired && 'text-destructive',
          isWarning && !isExpired && 'text-warning animate-pulse',
          !isWarning && !isExpired && 'text-foreground'
        )}
        role="timer"
        aria-live="polite"
      >
        {formattedTime}
      </div>

      {showProgress && total && (
        <Progress
          value={100 - progress}
          className={cn(
            'timer-progress h-1 [&>div]:transition-all [&>div]:duration-1000',
            isExpired && '[&>div]:bg-destructive',
            isWarning && !isExpired && '[&>div]:bg-warning'
          )}
        />
      )}

      {isExpired && (
        <span className="timer-expired text-xs text-destructive font-medium">Time's up!</span>
      )}
    </div>
  );
}

/**
 * Compact timer for inline use
 */
export function TimerBadge({
  remaining,
  isWarning = false,
  isExpired = false,
  className,
}: Pick<TimerDisplayProps, 'remaining' | 'isWarning' | 'isExpired' | 'className'>): React.ReactElement {
  return (
    <Badge
      variant="ghost"
      className={cn(
        'timer-badge gap-1 rounded-full border-transparent',
        isExpired && 'bg-destructive/20 text-destructive',
        isWarning && !isExpired && 'bg-warning/20 text-warning',
        !isWarning && !isExpired && 'bg-muted text-muted-foreground',
        className
      )}
    >
      <span className="timer-badge-indicator w-1.5 h-1.5 rounded-full bg-current" />
      <span className="font-mono">{formatTime(remaining)}</span>
    </Badge>
  );
}
